import React, { useState, useEffect } from 'react';

interface OverridePolicyStepProps {
  initialOverridePolicy?: Record<string, string>;
  provider: string;
  model: string;
  onChange: (data: {
    overridePolicy: Record<string, string> | null;
  }) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
}

type PolicyOption = 'gateway_wins' | 'client_wins' | 'inject_if_missing' | 'None';

interface ParameterConfig {
  name: string;
  label: string;
  description: string;
  supportedModels?: string[];
}

const OverridePolicyStep: React.FC<OverridePolicyStepProps> = ({
  initialOverridePolicy = {},
  provider,
  model,
  onChange,
  onNext,
  onBack,
  onCancel
}) => {
  // Available parameters for override
  const parameters: ParameterConfig[] = [
    {
      name: 'temperature',
      label: 'Temperature',
      description: 'Controls randomness (0.0-2.0). Higher values make output more random.',
    },
    {
      name: 'max_tokens',
      label: 'Max Tokens',
      description: 'Maximum number of tokens in the response.',
    },
    {
      name: 'reasoning_effort',
      label: 'Reasoning Effort',
      description: 'Reasoning depth for o1/o3 models (low, medium, high).',
      supportedModels: ['o1', 'o3', 'gpt-5'],
    },
    {
      name: 'top_p',
      label: 'Top P',
      description: 'Nucleus sampling threshold (0.0-1.0).',
    },
    {
      name: 'frequency_penalty',
      label: 'Frequency Penalty',
      description: 'Penalizes repetition (-2.0 to 2.0).',
    },
    {
      name: 'presence_penalty',
      label: 'Presence Penalty',
      description: 'Encourages topic diversity (-2.0 to 2.0).',
    },
    {
      name: 'model',
      label: 'Model',
      description: 'Force specific model selection.',
    },
  ];

  const [policies, setPolicies] = useState<Record<string, PolicyOption>>(
    () => {
      const initial: Record<string, PolicyOption> = {};
      parameters.forEach(param => {
        initial[param.name] = (initialOverridePolicy[param.name] as PolicyOption) || 'None';
      });
      return initial;
    }
  );

  const [enableOverrides, setEnableOverrides] = useState(() => {
    return Object.values(initialOverridePolicy).some(v => v !== 'None');
  });

  useEffect(() => {
    // Filter out "None" values and pass to parent
    if (enableOverrides) {
      const activeOverrides = Object.entries(policies)
        .filter(([_, policy]) => policy !== 'None')
        .reduce((acc, [param, policy]) => {
          acc[param] = policy;
          return acc;
        }, {} as Record<string, string>);
      
      onChange({
        overridePolicy: Object.keys(activeOverrides).length > 0 ? activeOverrides : null,
      });
    } else {
      onChange({ overridePolicy: null });
    }
  }, [policies, enableOverrides]);

  const handlePolicyChange = (paramName: string, policy: PolicyOption) => {
    setPolicies(prev => ({
      ...prev,
      [paramName]: policy,
    }));
  };

  const isParameterSupported = (param: ParameterConfig): boolean => {
    if (!param.supportedModels) return true;
    return param.supportedModels.some(supportedModel => 
      model.toLowerCase().includes(supportedModel.toLowerCase())
    );
  };

  const getPolicyDescription = (policy: PolicyOption): string => {
    switch (policy) {
      case 'gateway_wins':
        return 'Always use gateway value, ignore client';
      case 'client_wins':
        return 'Prefer client value, use gateway as default';
      case 'inject_if_missing':
        return 'Only inject if client didn\'t provide';
      case 'None':
        return 'No override policy';
      default:
        return '';
    }
  };

  const getPolicyBadgeClass = (policy: PolicyOption): string => {
    switch (policy) {
      case 'gateway_wins':
        return 'policy-badge-strict';
      case 'client_wins':
        return 'policy-badge-flexible';
      case 'inject_if_missing':
        return 'policy-badge-default';
      case 'None':
        return 'policy-badge-none';
      default:
        return '';
    }
  };

  return (
    <div className="wizard-step override-policy-step">
      <h2>Parameter Override Policies</h2>
      <p className="step-description">
        Control how the gateway handles LLM request parameters from SDK clients. 
        This allows you to enforce governance policies, cost controls, and compliance requirements.
      </p>

      <div className="override-toggle-container">
        <label className="toggle-label">
          <input
            type="checkbox"
            checked={enableOverrides}
            onChange={(e) => setEnableOverrides(e.target.checked)}
            className="toggle-checkbox"
          />
          <span className="toggle-text">Enable Parameter Overrides</span>
        </label>
        <p className="help-text">
          When enabled, SDK clients can route requests through this pipe by setting their baseURL to: 
          <code>/llm/{'<pipe-id>'}/v1/chat/completions</code>
        </p>
      </div>

      {enableOverrides && (
        <div className="parameters-list">
          <div className="parameters-header">
            <h3>Configure Override Policies</h3>
            <div className="policy-legend">
              <span className="policy-badge policy-badge-strict">Gateway Wins</span>
              <span className="policy-badge policy-badge-flexible">Client Wins</span>
              <span className="policy-badge policy-badge-default">Inject If Missing</span>
              <span className="policy-badge policy-badge-none">None</span>
            </div>
          </div>

          {parameters.map(param => {
            const isSupported = isParameterSupported(param);
            const currentPolicy = policies[param.name];
            
            return (
              <div 
                key={param.name} 
                className={`parameter-row ${!isSupported ? 'parameter-disabled' : ''}`}
              >
                <div className="parameter-info">
                  <div className="parameter-name-row">
                    <strong>{param.label}</strong>
                    {!isSupported && (
                      <span className="unsupported-badge">Not supported for {model}</span>
                    )}
                  </div>
                  <p className="parameter-description">{param.description}</p>
                </div>
                
                <div className="parameter-policy-selector">
                  <select
                    value={currentPolicy}
                    onChange={(e) => handlePolicyChange(param.name, e.target.value as PolicyOption)}
                    disabled={!isSupported}
                    className="policy-select"
                  >
                    <option value="None">None</option>
                    <option value="gateway_wins">Gateway Wins</option>
                    <option value="client_wins">Client Wins</option>
                    <option value="inject_if_missing">Inject If Missing</option>
                  </select>
                  
                  {currentPolicy !== 'None' && (
                    <div className={`policy-description-badge ${getPolicyBadgeClass(currentPolicy)}`}>
                      {getPolicyDescription(currentPolicy)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!enableOverrides && (
        <div className="override-disabled-info">
          <p>
            ℹ️ Parameter overrides are disabled. This pipe will use standard request forwarding 
            without inspecting or modifying client parameters.
          </p>
        </div>
      )}

      <div className="step-actions">
        <button onClick={onCancel} className="btn-cancel">
          Cancel
        </button>
        <button onClick={onBack} className="btn-back">
          ← Back
        </button>
        <button onClick={onNext} className="btn-next">
          Next →
        </button>
      </div>

      <style>{`
        .override-policy-step {
          max-width: 900px;
          margin: 0 auto;
        }

        .step-description {
          color: #666;
          margin-bottom: 24px;
          line-height: 1.6;
        }

        .override-toggle-container {
          background: #f5f5f5;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 24px;
        }

        .toggle-label {
          display: flex;
          align-items: center;
          cursor: pointer;
          margin-bottom: 12px;
        }

        .toggle-checkbox {
          width: 20px;
          height: 20px;
          cursor: pointer;
          margin-right: 12px;
        }

        .toggle-text {
          font-size: 16px;
          font-weight: 600;
        }

        .help-text {
          color: #666;
          font-size: 14px;
          margin: 0;
        }

        .help-text code {
          background: #e0e0e0;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 13px;
        }

        .parameters-list {
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 24px;
        }

        .parameters-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid #eee;
        }

        .parameters-header h3 {
          margin: 0;
        }

        .policy-legend {
          display: flex;
          gap: 8px;
        }

        .policy-badge {
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .policy-badge-strict {
          background: #ffebee;
          color: #c62828;
        }

        .policy-badge-flexible {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .policy-badge-default {
          background: #e3f2fd;
          color: #1565c0;
        }

        .policy-badge-none {
          background: #f5f5f5;
          color: #757575;
        }

        .parameter-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          padding: 20px;
          border-bottom: 1px solid #eee;
          align-items: start;
        }

        .parameter-row:last-child {
          border-bottom: none;
        }

        .parameter-disabled {
          opacity: 0.5;
        }

        .parameter-info {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .parameter-name-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .unsupported-badge {
          background: #fff3cd;
          color: #856404;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
        }

        .parameter-description {
          margin: 0;
          color: #666;
          font-size: 14px;
          line-height: 1.5;
        }

        .parameter-policy-selector {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .policy-select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }

        .policy-select:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        .policy-description-badge {
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 13px;
          text-align: center;
        }

        .override-disabled-info {
          background: #f9f9f9;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 24px;
          text-align: center;
          color: #666;
        }

        .step-actions {
          display: flex;
          justify-content: space-between;
          margin-top: 32px;
          padding-top: 24px;
          border-top: 1px solid #eee;
        }

        .btn-cancel,
        .btn-back,
        .btn-next {
          padding: 12px 24px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
        }

        .btn-cancel {
          background: transparent;
          color: #666;
          border: 1px solid #ddd;
        }

        .btn-cancel:hover {
          background: #f5f5f5;
        }

        .btn-back {
          background: transparent;
          color: #1976d2;
          border: 1px solid #1976d2;
        }

        .btn-back:hover {
          background: #e3f2fd;
        }

        .btn-next {
          background: #1976d2;
          color: white;
        }

        .btn-next:hover {
          background: #1565c0;
        }
      `}</style>
    </div>
  );
};

export default OverridePolicyStep;
